import React, { useState, useRef, useEffect } from "react";
import {
  Container,
  Typography,
  Box,
  Button,
  TextField,
  InputAdornment,
  Divider,
  Grid,
} from "@mui/material";
import Header from "../components/Admin/header/Header";
import Sidebar from "../components/Admin/Sidebar/Sidebar";
import { Link } from "react-router-dom";
import { Search, X } from "lucide-react";

const Master = () => {
  const [search, setSearch] = useState("");
  const [searchBy, setSearchBy] = useState("name");
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const searchContainerRef = useRef(null);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchContainerRef.current && !searchContainerRef.current.contains(event.target)) {
        setIsSearchFocused(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const BUTTON_LIST = [
    {
      id: "1",
      label: "Item Master",
      path: "item",
    },
    {
      id: "2",
      label: "Department Master",
      path: "department",
    },
    {
      id: "3",
      label: "Unit Master",
      path: "unit",
    },
    {
      id: "4",
      label: "Category Master",
      path: "category",
    },
    {
      id: "5",
      label: "Sub Category Master",
      path: "sub-category",
    },
    {
      id: "6",
      label: "Ward Master",
      path: "ward",
    },
    {
      id: "7",
      label: "Bed Category Master",
      path: "bed-category",
    },
    {
      id: "8",
      label: "Bed Sub Category Master",
      path: "bed-sub-category",
    },
    {
      id: "9",
      label: "Bed Numbers",
      path: "bed-sub-category/bed-number-one",
    },
    {
      id: "10",
      label: "Bed Number Master",
      path: "bed-sub-category/bed-number-two",
    },
    {
      id: "11",
      label: "Visit Type Master",
      path: "visit-type",
    },
    {
      id: "12",
      label: "Company Master",
      path: "company",
    },
    {
      id: "12a",
      label: "Supplier Master",
      path: "supplier",
    },
    {
      id: "13",
      label: "Sharing Master",
      path: "sharing",
    },
    {
      id: "14",
      label: "Contract Master",
      path: "contract",
    },
    {
      id: "15a",
      label: "Relief Master",
      path: "relief",
    },
    {
      id: "15",
      label: "Policy Master",
      path: "policy",
    },
    {
      id: "16",
      label: "Country Master",
      path: "country",
    },
    {
      id: "17",
      label: "State Master",
      path: "state",
    },
    {
      id: "18",
      label: "City Master",
      path: "city",
    },
    {
      id: "19",
      label: "Relationship Master",
      path: "relationship",
    },
    {
      id: "20",
      label: "Blood Group Master",
      path: "blood-group",
    },
    {
      id: "21",
      label: "Doctor Reference Master",
      path: "doctor-reference",
    },
    {
      id: "22",
      label: "Transaction Type Master",
      path: "transaction-type",
    },
    {
      id: "23",
      label: "Dynamic Template Master",
      path: "dynamic-template",
    },
  ];

  return (
    <Container maxWidth={false} disableGutters>
      <Header />
      <Box sx={{ display: "flex" }}>
        <Box>
          <Sidebar />
        </Box>
        <Box sx={{ 
          width: "100%", 
          pr: { xs: "10px", sm: "20px", md: "40px", lg: "60px", xl: "90px" },
          pl: { xs: "10px", sm: "20px", md: "20px" },
          maxWidth: "100vw",
          overflow: "hidden"
        }}>
          {/* Heading */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              my: "20px",
              color: "#5D6A82",
            }}
          >
            <Typography sx={{ 
              color: "#5D6A82", 
              fontFamily: "Open Sans",
              fontWeight: 600,
              fontSize: "22px",
              lineHeight: "29px",
              letterSpacing: "0px"
            }}>Master Settings</Typography>
          </Box>
          {/* Search */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              my: "20px",
            }}
          >
            <Box
              ref={searchContainerRef}
              sx={{
                display: "flex",
                backgroundColor: "white",
                border: isSearchFocused ? "2px solid #0179B0" : "1px solid #e0e0e0",
                borderRadius: "8px",
                alignItems: "center",
                position: "relative",
                boxShadow: isSearchFocused 
                  ? "0 4px 12px rgba(1, 121, 176, 0.15)" 
                  : "0 2px 4px rgba(0, 0, 0, 0.05)",
                transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                "&:hover": {
                  boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
                  borderColor: "#c0c0c0"
                }
              }}
            >
              {/* Search Input */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  padding: "12px 16px",
                  width: "300px",
                  transition: "all 0.3s ease",
                  gap: "10px"
                }}
              >
                <Search 
                  size={20} 
                  style={{ 
                    color: isSearchFocused ? "#0179B0" : "#666",
                    transition: "color 0.3s ease",
                    flexShrink: 0
                  }} 
                />
                <input
                  type="text"
                  placeholder="Search by Name"
                  value={search || ""}
                  onChange={(e) => setSearch(e.target.value)}
                  onFocus={() => setIsSearchFocused(true)}
                  style={{
                    border: "none",
                    outline: "none",
                    background: "transparent",
                    width: "100%",
                    fontSize: "14px",
                    color: "#333",
                    padding: "0",
                    minHeight: "24px",
                    touchAction: "manipulation",
                    WebkitTapHighlightColor: "transparent"
                  }}
                />
                {search && (
                  <X 
                    size={18} 
                    style={{ 
                      cursor: "pointer", 
                      color: "#999",
                      flexShrink: 0,
                      padding: "2px",
                      transition: "all 0.2s ease",
                      touchAction: "manipulation"
                    }}
                    onClick={() => setSearch("")}
                    onMouseEnter={(e) => e.target.style.color = "#0179B0"}
                    onMouseLeave={(e) => e.target.style.color = "#999"}
                  />
                )}
              </Box>
            </Box>
          </Box>
          <Box>
            <Typography sx={{ mb: "20px", color: "#5D6A82" }}>
              Categories
            </Typography>
            <Grid container spacing={3}>
              {BUTTON_LIST?.filter((option) => {
                if (!search) return true;
                const searchLower = search.toLowerCase();
                if (searchBy === "name") {
                  return option?.label?.toLowerCase().includes(searchLower);
                }
                return true;
              }).map((option, index) => (
                <Grid item xs={12} sm={6} md={4} key={option?.id || index}>
                  <Link to={option?.path}>
                    <Button
                      variant="outlined"
                      fullWidth
                      size="large"
                      sx={{
                        color: "black",
                        textTransform: "none",
                      }}
                    >
                      {option?.label || ""}
                    </Button>
                  </Link>
                </Grid>
              ))}
            </Grid>
          </Box>
        </Box>
      </Box>
    </Container>
  );
};

export default Master;
